import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  Search,
  Refresh,
  Visibility,
  Edit,
  Delete,
} from '@mui/icons-material';

export interface Column<T> {
  id: keyof T | string;
  label: string;
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  format?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  totalCount?: number;
  page?: number;
  rowsPerPage?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  onSearch?: (query: string) => void;
  onRefresh?: () => void;
  onView?: (row: T) => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  extraActions?: (row: T) => React.ReactNode;
  searchPlaceholder?: string;
  emptyMessage?: string;
  rowKey?: keyof T;
}

function DataTable<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  totalCount,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  onSearch,
  onRefresh,
  onView,
  onEdit,
  onDelete,
  extraActions,
  searchPlaceholder = 'Pesquisar...',
  emptyMessage = 'Nenhum registo encontrado',
  rowKey = 'id' as keyof T,
}: DataTableProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    onSearch?.(event.target.value);
  };

  const handleSort = (columnId: string) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
  };

  const hasActions = onView || onEdit || onDelete || extraActions;

  const getValue = (row: T, columnId: string): any => {
    const keys = columnId.split('.');
    let value: any = row;
    for (const key of keys) {
      value = value?.[key];
    }
    return value;
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Toolbar */}
      <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        {onSearch && (
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {onRefresh && (
          <Tooltip title="Atualizar">
            <IconButton onClick={onRefresh} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Table */}
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={String(column.id)}
                  align={column.align || 'left'}
                  style={{ minWidth: column.minWidth }}
                  sx={{ fontWeight: 600, bgcolor: 'action.hover' }}
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleSort(String(column.id))}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              {hasActions && (
                <TableCell
                  align="center"
                  sx={{ fontWeight: 600, bgcolor: 'action.hover', minWidth: 120 }}
                >
                  Ações
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (hasActions ? 1 : 0)}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (hasActions ? 1 : 0)}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography color="text.secondary">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow
                  hover
                  key={String(row[rowKey]) || index}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  {columns.map((column) => {
                    const value = getValue(row, String(column.id));
                    return (
                      <TableCell key={String(column.id)} align={column.align || 'left'}>
                        {column.format ? column.format(value, row) : value}
                      </TableCell>
                    );
                  })}
                  {hasActions && (
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        {onView && (
                          <Tooltip title="Ver">
                            <IconButton size="small" onClick={() => onView(row)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onEdit && (
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => onEdit(row)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onDelete && (
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onDelete(row)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {extraActions && extraActions(row)}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {onPageChange && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount || data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_: unknown, newPage: number) => onPageChange(newPage)}
          onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>) => onRowsPerPageChange?.(parseInt(e.target.value, 10))}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }: { from: number; to: number; count: number }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      )}
    </Paper>
  );
}

export default DataTable;

// Helper components for formatting
export const StatusChip: React.FC<{
  status: string;
  colorMap?: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'>;
}> = ({ status, colorMap }) => {
  const defaultColorMap: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
    active: 'success',
    A: 'success',
    completed: 'success',
    paid: 'success',
    inactive: 'default',
    I: 'default',
    pending: 'warning',
    P: 'warning',
    cancelled: 'error',
    C: 'error',
    failed: 'error',
    F: 'error',
  };

  const colors = colorMap || defaultColorMap;
  const color = colors[status] || 'default';

  return <Chip label={status} size="small" color={color} />;
};

export const CurrencyCell: React.FC<{ value: number; currency?: string }> = ({
  value,
  currency = 'CVE',
}) => {
  const formatted = new Intl.NumberFormat('pt-CV', {
    style: 'currency',
    currency,
  }).format(value);

  return (
    <Typography
      variant="body2"
      sx={{ fontWeight: value > 0 ? 600 : 400, color: value > 0 ? 'success.main' : 'inherit' }}
    >
      {formatted}
    </Typography>
  );
};

// parede: data escrita por pessoas (hora de CV marcada com Z) → mostrar tal e qual (UTC).
// Sem parede: instante real → hora de Cabo Verde, qualquer que seja o fuso do browser.
export const DateCell: React.FC<{ value: string | Date; includeTime?: boolean; parede?: boolean }> = ({
  value,
  includeTime = false,
  parede = false,
}) => {
  if (!value) return <span>-</span>;

  const date = new Date(value);
  const timeZone = parede ? 'UTC' : 'Atlantic/Cape_Verde';
  const formatted = includeTime
    ? date.toLocaleString('pt-PT', { timeZone })
    : date.toLocaleDateString('pt-PT', { timeZone });

  return <span>{formatted}</span>;
};
